/**
 * Canonical React Query key factories.
 * Never write literal queryKey arrays in hooks — use these so invalidation
 * from another feature can target the right subtree.
 */
export const qk = {
  features: {
    all: () => ["features"] as const,
  },
  profiles: {
    all: () => ["profiles"] as const,
    list: () => ["profiles", "list"] as const,
    detail: (id: string) => ["profiles", "detail", id] as const,
  },
  tasks: {
    detail: (id: string) => ["tasks", id] as const,
  },
  copilot: {
    summary: (sessionId: string) =>
      ["copilot", "summary", sessionId] as const,
  },
  visitCard: {
    card: (sessionId: string) => ["visit-card", sessionId] as const,
    references: () => ["visit-card", "references"] as const,
    designers: (dealerId: string) =>
      ["visit-card", "designers", dealerId] as const,
  },
  auth: {
    me: () => ["auth", "me"] as const,
  },
  launch: {
    intents: () => ["launch-intents"] as const,
  },
  partner: {
    launch: (token: string) => ["partner", "launch", token] as const,
  },
  dashboard: {
    all: () => ["dashboard"] as const,
    designers: (from: string | null, to: string | null, salonIds: string[] | null) =>
      ["dashboard", "designers", from, to, salonIds] as const,
    salonAnalytics: (
      from: string | null,
      to: string | null,
      salonIds: string[] | null,
    ) => ["dashboard", "salon-analytics", from, to, salonIds] as const,
    salonAnalyticsInterpretation: (
      from: string | null,
      to: string | null,
      salonIds: string[] | null,
    ) =>
      [
        "dashboard",
        "salon-analytics-interpretation",
        from,
        to,
        salonIds,
      ] as const,
    designerFlagStats: (
      from: string | null,
      to: string | null,
      salonIds: string[] | null,
    ) => ["dashboard", "designer-flag-stats", from, to, salonIds] as const,
    designerConversionStats: (
      from: string | null,
      to: string | null,
      salonIds: string[] | null,
    ) =>
      ["dashboard", "designer-conversion-stats", from, to, salonIds] as const,
    typicalIssues: (
      from: string | null,
      to: string | null,
      salonIds: string[] | null,
      limit: number,
    ) => ["dashboard", "typical-issues", from, to, salonIds, limit] as const,
    designerClients: (
      email: string,
      from: string | null,
      to: string | null,
    ) => ["dashboard", "designer-clients", email, from, to] as const,
    /** Prefix for cancel/invalidate across all date ranges of one designer. */
    designerClientsPrefix: (email: string) =>
      ["dashboard", "designer-clients", email] as const,
    clientMeetings: (
      email: string,
      clientProfile: string,
      from: string | null,
      to: string | null,
    ) =>
      [
        "dashboard",
        "client-meetings",
        email,
        clientProfile,
        from,
        to,
      ] as const,
    /** Prefix for cancel/invalidate across all date ranges of one client. */
    clientMeetingsPrefix: (email: string, clientProfile: string) =>
      ["dashboard", "client-meetings", email, clientProfile] as const,
    panelStats: (email: string, from: string | null, to: string | null) =>
      ["dashboard", "panel-stats", email, from, to] as const,
    meeting: (meetingId: string) =>
      ["dashboard", "meeting", meetingId] as const,
    meetingFeedback: (meetingId: string) =>
      ["dashboard", "meeting", meetingId, "feedback"] as const,
  },
} as const
